import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Server,
  MonitorCog,
  Network,
  Quote,
  CheckCircle2,
} from "lucide-react";
import { api, asArray } from "@/lib/api";
import { useSeo, ORG_JSON_LD } from "@/lib/seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LOGO_URL } from "@/components/Logo";

const iconMap = { ShieldCheck, Server, MonitorCog, Network };

const STATS = [
  { value: "120+", label: "Proyek Tersampaikan" },
  { value: "45+", label: "Mitra Pemerintahan & Korporasi" },
  { value: "10", label: "Tahun Pengalaman" },
  { value: "24/7", label: "Dukungan Teknis" },
];

const PROCESS = [
  {
    no: "01",
    title: "Konsultasi & Analisa",
    desc: "Memahami kebutuhan bisnis, audit infrastruktur, dan penyusunan dokumen kebutuhan.",
  },
  {
    no: "02",
    title: "Desain Solusi",
    desc: "Arsitektur sistem, pemilihan teknologi, dan rancangan anggaran biaya yang transparan.",
  },
  {
    no: "03",
    title: "Implementasi",
    desc: "Eksekusi proyek dengan metodologi terukur, pelaporan berkala, dan QA berlapis.",
  },
  {
    no: "04",
    title: "Pemeliharaan",
    desc: "Layanan dukungan jangka panjang, monitoring 24/7, dan optimisasi berkelanjutan.",
  },
];

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [portfolios, setPortfolios] = useState([]);

  useSeo({
    title: "Solusi IT, Sistem Informasi & Pengadaan TI",
    description:
      "CV. Komunika Teknik — mitra strategis transformasi digital untuk pemerintah, BUMN, dan korporasi di Indonesia. Pengembangan sistem informasi, pengadaan perangkat TI, konsultasi, dan infrastruktur.",
    path: "/",
    jsonLd: ORG_JSON_LD,
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(asArray(r.data))).catch(() => setServices([]));
    api
      .get("/portfolios", { params: { featured_only: true, limit: 3 } })
      .then((r) => setPortfolios(asArray(r.data)))
      .catch(() => setPortfolios([]));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-transparent" />
        </div>
        <div className="kt-container relative py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 kt-rise">
              <div className="kt-eyebrow text-[#FCA5A5]">CV. KOMUNIKA TEKNIK</div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
                Mitra Strategis untuk{" "}
                <span className="text-[#DC2626]">Transformasi Digital</span>{" "}
                Instansi & Korporasi Anda.
              </h1>
              <p className="mt-6 text-zinc-300 text-base md:text-lg max-w-2xl leading-relaxed">
                Penyedia layanan teknologi informasi, pengembangan sistem
                informasi, dan pengadaan perangkat TI sejak satu dekade.
                Berpengalaman melayani pemerintah, BUMN, dan perusahaan
                manufaktur skala nasional.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/kontak"
                  className="kt-btn-primary"
                  data-testid="hero-quote-button"
                >
                  Minta Penawaran <ArrowRight size={18} />
                </Link>
                <Link
                  to="/layanan"
                  className="kt-btn-outline border-white/30 text-white hover:bg-white/10 hover:border-white"
                  data-testid="hero-services-button"
                >
                  Jelajah Layanan
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                {STATS.map((s) => (
                  <div key={s.label} data-testid={`hero-stat-${s.label.replace(/\s+/g,'-').toLowerCase()}`}>
                    <div className="kt-stat text-3xl md:text-4xl text-white">
                      {s.value}
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-400 mt-2">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="border border-white/10 bg-white/5 backdrop-blur-md p-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#DC2626] grid place-items-center text-xs font-mono-kt">
                      KT
                    </div>
                    <div className="text-xs font-mono-kt text-zinc-400">
                      ISO-COMPLIANT · LKPP REGISTERED
                    </div>
                  </div>
                  <div className="mt-6 grid grid-cols-2 gap-4 text-xs text-zinc-300">
                    {[
                      "Sistem Informasi",
                      "Pengadaan TI",
                      "Konsultasi",
                      "Infrastruktur",
                      "Lisensi Resmi",
                      "Support 24/7",
                    ].map((tag) => (
                      <div
                        key={tag}
                        className="flex items-center gap-2 border-l-2 border-[#DC2626] pl-3 py-1"
                      >
                        <CheckCircle2 size={14} className="text-[#DC2626]" />
                        {tag}
                      </div>
                    ))}
                  </div>
                  <div className="mt-8">
                    <img
                      src={LOGO_URL}
                      alt="Komunika Teknik"
                      className="w-24 h-24 object-contain mx-auto opacity-90"
                    />
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#DC2626] -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="kt-section bg-white" data-testid="about-section">
        <div className="kt-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kt-eyebrow">Tentang Kami</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 leading-tight">
              Sepuluh tahun mendampingi transformasi digital di Indonesia.
            </h2>
            <div className="kt-divider-red mt-6" />
            <p className="mt-6 text-[#52525B] leading-relaxed">
              CV. Komunika Teknik berdiri sebagai mitra strategis untuk
              kebutuhan teknologi informasi instansi pemerintah, BUMN, dan
              perusahaan swasta. Kami menggabungkan keahlian engineering,
              konsultasi bisnis, dan pengadaan TI dalam satu pintu solusi
              terintegrasi.
            </p>
            <p className="mt-4 text-[#52525B] leading-relaxed">
              Komitmen kami adalah memberikan solusi yang bukan hanya
              fungsional — tapi juga andal, aman, dan selaras dengan strategi
              jangka panjang organisasi Anda.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                { label: "Engineer Bersertifikat", v: "30+" },
                { label: "Tingkat Kepuasan Klien", v: "98%" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="kt-stat text-3xl text-[#18181B]">{s.v}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#52525B] mt-2">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&w=1400"
                alt="IT Consulting"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-start gap-4">
                  <Quote className="text-[#DC2626] shrink-0" size={28} />
                  <div>
                    <p className="text-[#18181B] font-semibold leading-snug">
                      “Solusi yang dikerjakan profesional, tepat waktu, dan
                      didukung purna jual yang responsif.”
                    </p>
                    <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#52525B]">
                      — Direktur IT, BUMN Sektor Energi
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        className="kt-section bg-[#FAFAFA] kt-grid-bg"
        data-testid="home-services-section"
      >
        <div className="kt-container">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-12">
            <div>
              <div className="kt-eyebrow">Layanan Kami</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 max-w-2xl leading-tight">
                Tiga pilar layanan teknologi informasi untuk bisnis modern.
              </h2>
            </div>
            <Link
              to="/layanan"
              className="kt-btn-outline self-start"
              data-testid="home-all-services-link"
            >
              Semua Layanan <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Server;
              return (
                <Link
                  key={s.id}
                  to={`/layanan/${s.slug}`}
                  className="kt-card p-7 group"
                  data-testid={`home-service-card-${s.slug}`}
                >
                  <div className="w-12 h-12 bg-[#18181B] text-white grid place-items-center group-hover:bg-[#DC2626] transition-colors">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display font-bold text-lg mt-6 leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#52525B] leading-relaxed line-clamp-4">
                    {s.summary}
                  </p>
                  <div className="mt-5 text-xs uppercase tracking-[0.18em] text-[#DC2626] font-semibold inline-flex items-center gap-1.5">
                    Pelajari <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORTFOLIO HIGHLIGHTS */}
      <section className="kt-section bg-white" data-testid="home-portfolio-section">
        <div className="kt-container">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-12">
            <div>
              <div className="kt-eyebrow">Sorotan Proyek</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 max-w-2xl leading-tight">
                Karya yang telah kami selesaikan bersama mitra.
              </h2>
            </div>
            <Link
              to="/portfolio"
              className="kt-btn-outline self-start"
              data-testid="home-all-portfolio-link"
            >
              Semua Proyek <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {portfolios.map((p) => (
              <Link
                key={p.id}
                to={`/portfolio/${p.slug}`}
                className="kt-card group overflow-hidden"
                data-testid={`home-portfolio-card-${p.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#52525B]">
                    <span>{p.category}</span>
                    <span className="w-1 h-1 bg-[#DC2626] rounded-full" />
                    <span>{p.year}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-3 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#52525B] mt-2 line-clamp-2">
                    {p.summary}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="kt-section bg-[#0F172A] text-white" data-testid="process-section">
        <div className="kt-container">
          <div className="kt-eyebrow text-[#FCA5A5]">Metodologi Kami</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 max-w-2xl leading-tight">
            Empat langkah delivery yang terstruktur dan transparan.
          </h2>
          <div className="kt-divider-red mt-6" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-14">
            {PROCESS.map((p) => (
              <div
                key={p.no}
                className="bg-[#0F172A] p-8 hover:bg-[#1e293b] transition-colors"
                data-testid={`process-step-${p.no}`}
              >
                <div className="text-[#DC2626] font-mono-kt text-sm tracking-widest">
                  STEP / {p.no}
                </div>
                <h3 className="font-display font-bold text-xl mt-4">
                  {p.title}
                </h3>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="kt-section bg-white" data-testid="home-cta-section">
        <div className="kt-container">
          <div className="border border-[#E4E4E7] bg-[#FAFAFA] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="kt-eyebrow">Mulai Proyek Anda</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 leading-tight">
                Siap mendiskusikan kebutuhan TI organisasi Anda?
              </h2>
              <p className="mt-4 text-[#52525B] max-w-xl">
                Tim kami siap menyusun proposal dan estimasi biaya yang sesuai
                dengan skala dan urgensi proyek Anda.
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
              <Link
                to="/kontak"
                className="kt-btn-primary"
                data-testid="home-cta-contact-link"
              >
                Hubungi Kami <ArrowRight size={18} />
              </Link>
              <a
                href="mailto:info@komunikateknik.online"
                className="text-sm text-[#52525B] font-mono-kt"
              >
                info@komunikateknik.online
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
